/**
 * Coup de pouce — Attendu 1107 « résoudre x + b = c et ax = c » (automatisme
 * 4e, l. 702). DEUX pages STATIQUES, un cas par page, partagées par la
 * série flash et le QCM « Quelle solution ? » ; PAS de fiche sur le QCM
 * « Quel calcul ? » (calcul_qcm_unique : la fiche donnerait la réponse,
 * doctrine règle 9).
 *
 * L'image que l'élève a déjà : le schéma en barres de 1046 (« 14 + … = 32 »)
 * et celui de 1049 (« 4 parts égales qui font 13 »), avec la lettre à la
 * place du trou. Page 1 « Ce qui manque » : la barre 32, dessous 14 et x ;
 * « x = 32 − 14 = 18 » ; « x, c'est ce qui manque pour arriver à 32 :
 * 32 − 14. » ; « Je vérifie : 18 + 14 = 32. » Page 2 « 4 parts égales qui
 * font 13 » : la barre 13, dessous 4 parts étiquetées x ; « x = 13 ÷ 4 =
 * 13/4 » ; « 4 parts égales qui font 13 : une part, c'est 13 ÷ 4. » ;
 * « Je vérifie : 4 × 13/4 = 13. » Les deux fiches voisines (1046, 1049)
 * disent la même chose.
 *
 * SACRIFIÉS : (14 ; 32) et (4 ; 13), sous toutes leurs formes et lettres
 * (gen_1107, SACRIFIES, auto-testé par gen1107.self.test.js). Largeur bornée
 * à 340 px, tailles de 1048, fractions empilées.
 */

const ORANGE = '#EA580C';
const ORANGE_CLAIR = '#FED7AA';
const BLEU = '#2563EB';
const BLEU_CLAIR = '#DBEAFE';
const VERT = '#15803D';
const GRIS = '#E2E8F0';
const ENCRE = '#1E293B';

const XB = 40;
const WB = 320;
/** La lettre, dans la police italique de KaTeX (déclarée par katex.min.css) : le même x que dans les énoncés. */
const X = `<tspan font-family="KaTeX_Math, 'Times New Roman', serif" font-style="italic">x</tspan>`;

/** Une fraction empilée, une couleur. */
const frac = (x, y, n, d, couleur, taille, demi) => `
  <text x="${x}" y="${y - 4}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${n}</text>
  <line x1="${x - demi}" y1="${y}" x2="${x + demi}" y2="${y}" stroke="${couleur}" stroke-width="1.8"/>
  <text x="${x}" y="${y + taille + 1}" font-size="${taille}" font-weight="bold" text-anchor="middle" fill="${couleur}">${d}</text>`;

// ─── Page 1 : x + 14 = 32 ─────────────────────────────────────────────────

const SVG_SOMME = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 250" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="x plus 14 égale 32. Schéma en barres : une barre entière de 32, et dessous la même longueur coupée en un morceau 14 et un morceau x, en orange. x égale 32 moins 14 égale 18. x, c'est ce qui manque pour arriver à 32 : 32 moins 14. Je vérifie : 18 plus 14 égale 32.">
  <text x="200" y="30" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${ORANGE}">${X}</tspan> + <tspan fill="${BLEU}">14</tspan> = <tspan fill="${VERT}">32</tspan></text>
  <rect x="${XB}" y="48" width="${WB}" height="36" fill="${GRIS}" stroke="${ENCRE}" stroke-width="1.5"/>
  <text x="200" y="73" font-size="15" font-weight="bold" text-anchor="middle" fill="${VERT}">32</text>
  <rect x="${XB}" y="96" width="140" height="36" fill="${BLEU_CLAIR}" stroke="${ENCRE}" stroke-width="1.5"/>
  <text x="${XB + 70}" y="121" font-size="15" font-weight="bold" text-anchor="middle" fill="${BLEU}">14</text>
  <rect x="${XB + 140}" y="96" width="180" height="36" fill="${ORANGE_CLAIR}" stroke="${ORANGE}" stroke-width="2"/>
  <text x="${XB + 230}" y="122" font-size="17" font-weight="bold" text-anchor="middle" fill="${ORANGE}">${X}</text>

  <!-- le résultat -->
  <text x="200" y="180" font-size="20" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${ORANGE}">${X}</tspan> = <tspan fill="${VERT}">32</tspan> − <tspan fill="${BLEU}">14</tspan> = <tspan fill="${ORANGE}">18</tspan></text>

  <!-- le geste, puis la vérification -->
  <text x="200" y="216" font-size="12" text-anchor="middle" fill="${ENCRE}"><tspan font-weight="bold" fill="${ORANGE}">${X}</tspan>, c'est ce qui manque pour arriver à <tspan font-weight="bold" fill="${VERT}">32</tspan> : 32 − 14.</text>
  <text x="200" y="240" font-size="12" text-anchor="middle" fill="${ENCRE}">Je vérifie : <tspan font-weight="bold" fill="${ORANGE}">18</tspan> + 14 = 32.</text>
</svg>`.trim();

// ─── Page 2 : 4x = 13 ─────────────────────────────────────────────────────

const NB = 4;
const WM = WB / NB;
const PARTS = Array.from({ length: NB }, (_, k) => {
    const x = XB + k * WM;
    return `<rect x="${x}" y="96" width="${WM}" height="36" fill="${ORANGE_CLAIR}" stroke="${ORANGE}" stroke-width="2"/>
  <text x="${x + WM / 2}" y="122" font-size="17" font-weight="bold" text-anchor="middle" fill="${ORANGE}">${X}</text>`;
}).join('\n  ');

const SVG_PRODUIT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 268" width="100%" style="max-width:340px;height:auto;display:block" role="img"
     aria-label="4 x égale 13. Schéma en barres : une barre entière de 13, et dessous la même longueur coupée en 4 parts égales, chacune étiquetée x, en orange. x égale 13 divisé par 4 égale 13 sur 4. 4 parts égales qui font 13 : une part, c'est 13 divisé par 4. Je vérifie : 4 fois 13 sur 4 égale 13.">
  <text x="200" y="30" font-size="16" font-weight="bold" text-anchor="middle" fill="${ENCRE}"><tspan fill="${BLEU}">4</tspan><tspan fill="${ORANGE}">${X}</tspan> = <tspan fill="${VERT}">13</tspan></text>
  <rect x="${XB}" y="48" width="${WB}" height="36" fill="${GRIS}" stroke="${ENCRE}" stroke-width="1.5"/>
  <text x="200" y="73" font-size="15" font-weight="bold" text-anchor="middle" fill="${VERT}">13</text>
  ${PARTS}

  <!-- le résultat -->
  <text x="212" y="183" font-size="20" font-weight="bold" text-anchor="end" fill="${ENCRE}"><tspan fill="${ORANGE}">${X}</tspan> = <tspan fill="${VERT}">13</tspan> ÷ <tspan fill="${BLEU}">4</tspan> =</text>
  ${frac(232, 176, 13, 4, ORANGE, 15, 12)}

  <!-- le geste, puis la vérification -->
  <text x="200" y="226" font-size="12" text-anchor="middle" fill="${ENCRE}"><tspan font-weight="bold" fill="${BLEU}">4 parts égales</tspan> qui font <tspan font-weight="bold" fill="${VERT}">13</tspan> : une part, c'est 13 ÷ 4.</text>
  <text x="218" y="256" font-size="12" text-anchor="end" fill="${ENCRE}">Je vérifie : <tspan font-weight="bold" fill="${BLEU}">4</tspan> ×</text>
  ${frac(232, 251, 13, 4, ORANGE, 10, 7)}
  <text x="244" y="256" font-size="12" fill="${ENCRE}">= <tspan font-weight="bold" fill="${VERT}">13</tspan>.</text>
</svg>`.trim();

const AIDE = {
    pages: [
        { titre: 'Ce qui manque', svg: SVG_SOMME },
        { titre: '4 parts égales qui font 13', svg: SVG_PRODUIT },
    ],
};

const CP_1107 = {
    flash_texte_trou_saisie: AIDE,
    solution_qcm_unique: AIDE,
    // calcul_qcm_unique : pas de fiche (elle donnerait le calcul, règle 9)
};

export default CP_1107;
